import { BigNum } from '@drift-labs/sdk';
import { VaultDepositor } from '@drift-labs/vaults-sdk';
import { MarketId, UIMarket } from '@drift/common';
import dayjs from 'dayjs';
import {
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	ExternalUiVaultConfig,
	PeriodApys,
	UiVaultConfig,
	VaultStats,
} from 'src/@types/vaults';
import { BalanceDisplayType } from 'src/@types/types';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import {
	useAllVaultStats,
	VaultStatWithConfig,
} from 'src/hooks/query/vaults/derived/useVaultStats';
import {
	getApyToUse,
	getAuthorityVaultBalance,
	getVaultAge,
} from 'src/utils/vault/math';
import { cm, cj } from 'src/utils/classNames';
import {
	useDrawersStore,
	useDrawerToggler,
} from '../../../stores/useDrawersStore';
import { SortDirection } from '../../Icons/SortIcon';
import SortCaret from '../../Tables/SortCaret';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import MarketIcon from '../../Utils/MarketIcon';
import InlineLoadingBar from '../../InlineLoadingBar/InlineLoadingBar';

import { VaultTableVaultComparisonCard } from '../VaultCard';
import { VaultTvlProgressDisplay } from '../VaultTvlProgressDisplay';
import VaultViewActions from '../VaultViewActions';
import { VaultInfoCell } from './VaultTableCells';
import Divider from 'src/components/primitives/Divider/Divider';
import { getApyColumnHeader } from '../utils';
import { SortableHeaderType } from '../type';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';

const NEW_VAULTS_TITLE = 'New Vaults (<30D)';
const NEW_VAULTS_DESCRIPTION =
	'These vaults are less than 30 days old, the target APY set by the vault manager is used as a reference instead.';

const getHeaders = (targetApyPeriod = '90d') => [
	{ label: 'Vault' },
	{ label: 'TVL', sortKey: 'tvl' as SortableHeaderType },
	{
		label: getApyColumnHeader(targetApyPeriod),
		sortKey: 'apy' as SortableHeaderType,
	},
	{ label: 'Age', sortKey: 'age' as SortableHeaderType },
	{ label: 'Your Balance', sortKey: 'balance' as SortableHeaderType },
	{ label: 'Action' },
];

const VaultRow = ({
	vaultStat,
	apyPeriod,
	vaultDepositorAccounts,
	getOraclePrice,
}: {
	vaultStat: VaultStats & { uiVaultConfig: UiVaultConfig };
	apyPeriod: keyof PeriodApys;
	vaultDepositorAccounts: VaultDepositor[];
	getOraclePrice: (marketId: MarketId | undefined) => BigNum | undefined;
}) => {
	const { apy, isUsingTemporaryApy, apyString } = getApyToUse(
		vaultStat.uiVaultConfig,
		vaultStat.apys[apyPeriod as keyof PeriodApys],
		vaultStat.numOfVaultSnapshots ?? 0
	);

	const authorityVaultBalance = getAuthorityVaultBalance(
		vaultDepositorAccounts,
		vaultStat,
		'base'
	);

	const authorityVaultBalanceQuote = getAuthorityVaultBalance(
		vaultDepositorAccounts,
		vaultStat,
		'quote',
		getOraclePrice
	);

	const toggleVaultDetails = useDrawerToggler('vaultDetails');
	const set = useDrawersStore((state) => state.set);

	const { captureEvent } = usePostHogCapture();

	const handleViewVault = () => {
		set((store) => {
			store.vaultDetails.vault = vaultStat.uiVaultConfig;
		});

		toggleVaultDetails(true);

		captureEvent('open_strat_vault_side_panel');
	};

	const market = UIMarket.createSpotMarket(
		vaultStat.uiVaultConfig.depositAsset
	);

	return (
		<div
			className={cj(
				'col-span-12 grid grid-cols-subgrid',
				'px-md py-lg',
				'cursor-pointer bg-container-bg hover:bg-button-disabled'
			)}
			onClick={handleViewVault}
		>
			<VaultInfoCell
				uiVaultConfig={vaultStat.uiVaultConfig}
				numOfVaultSnapshots={vaultStat.numOfVaultSnapshots}
			/>

			<VaultTvlProgressDisplay
				vaultStats={vaultStat}
				uiVaultConfig={vaultStat.uiVaultConfig}
				className="pr-3xl"
				inDesktopRow
			/>

			<div className="flex items-center typo-t3">
				<div
					className={cm(
						'flex items-center gap-1',
						isUsingTemporaryApy
							? 'text-purple-link'
							: apy >= 0
								? 'text-text-positive-green-button'
								: 'text-negative-red'
					)}
				>
					<span>{apyString}</span>
				</div>
			</div>

			<div className="flex items-center typo-t3">
				<span className="text-text-default">
					{vaultStat.numOfVaultSnapshots
						? `${vaultStat.numOfVaultSnapshots} days`
						: !vaultStat.uiVaultConfig.isDriftVaultsProgramVault
							? `${dayjs().diff(
									dayjs.unix(
										(vaultStat.uiVaultConfig as ExternalUiVaultConfig).startTs
									),
									'days'
								)} days`
							: '-'}
				</span>
			</div>

			<div className="flex items-center">
				{authorityVaultBalance.eqZero() ? (
					<span className="typo-b4 text-text-disabled">-</span>
				) : (
					<div className="flex flex-col gap-xs">
						<div className="flex items-center gap-xs typo-t3">
							<MarketIcon
								marketSymbol={
									UIMarket.createSpotMarket(
										vaultStat.uiVaultConfig.depositAsset
									).market.symbol
								}
								className="h-4 w-4"
							/>
							<span
								className={cm(
									'typo-t3',
									authorityVaultBalance.eqZero()
										? 'text-text-disabled'
										: 'text-text-default'
								)}
							>
								{market.symbol === 'USDC'
									? authorityVaultBalance.toFixed(2)
									: authorityVaultBalance.toMillified(4)}
							</span>
						</div>

						<span className="typo-b4 text-text-secondary">
							{authorityVaultBalanceQuote.toNotional()}
						</span>
					</div>
				)}
			</div>

			<div className="flex items-center justify-end">
				<VaultViewActions
					uiVaultConfig={vaultStat.uiVaultConfig}
					condensed={true}
					classes={{
						buttons: 'text-2xs',
					}}
				/>
			</div>
		</div>
	);
};

function sortVaults(
	a: VaultStatWithConfig,
	b: VaultStatWithConfig,
	sortDirection: SortDirection,
	sortType: SortableHeaderType,
	targetApyPeriod: string,
	vaultDepositorAccounts: VaultDepositor[],
	getOraclePrice: (marketId: MarketId | undefined) => BigNum | undefined
) {
	const tvlSort = () => {
		const res = b.tvlQuote.sub(a.tvlQuote).toNum();

		return sortDirection === 'descending' ? res : res * -1;
	};

	const apySort = () => {
		const { apy: vaultAApy } = getApyToUse(
			a.uiVaultConfig,
			a.apys[targetApyPeriod as keyof PeriodApys],
			a.numOfVaultSnapshots ?? 0
		);
		const { apy: vaultBApy } = getApyToUse(
			b.uiVaultConfig,
			b.apys[targetApyPeriod as keyof PeriodApys],
			b.numOfVaultSnapshots ?? 0
		);

		const res = vaultBApy - vaultAApy;
		return sortDirection === 'ascending' ? res * -1 : res; // default to descending
	};

	const ageSort = () => {
		const res = getVaultAge(b) - getVaultAge(a);
		return sortDirection === 'ascending' ? res * -1 : res; // default to descending
	};

	const balanceSort = () => {
		const authorityVaultBalanceA = getAuthorityVaultBalance(
			vaultDepositorAccounts,
			a,
			'quote',
			getOraclePrice
		);
		const authorityVaultBalanceB = getAuthorityVaultBalance(
			vaultDepositorAccounts,
			b,
			'quote',
			getOraclePrice
		).shiftTo(authorityVaultBalanceA.precision);

		const res = authorityVaultBalanceB.sub(authorityVaultBalanceA).toNum();
		return sortDirection === 'ascending' ? res * -1 : res; // default to descending
	};

	switch (sortType) {
		case 'tvl':
			return tvlSort();
		case 'apy':
			return apySort();
		case 'age':
			return ageSort();
		case 'balance':
			return balanceSort();
		default:
			return apySort();
	}
}

const VaultsTableBase = ({
	sortedVaultsList,
	areInitialVaultsStatsLoaded,
	sortType,
	setSortType,
	sortDirection,
	setSortDirection,
	targetApyPeriod,
	vaultDepositorAccounts,
	isNewVaults,
	getOraclePrice,
	topContent,
}: {
	sortedVaultsList: VaultStatWithConfig[];
	areInitialVaultsStatsLoaded: boolean;
	sortType: SortableHeaderType;
	setSortType: (type: SortableHeaderType) => void;
	sortDirection: SortDirection;
	setSortDirection: (direction: SetStateAction<SortDirection>) => void;
	targetApyPeriod: string;
	vaultDepositorAccounts: VaultDepositor[];
	isNewVaults?: boolean;
	getOraclePrice: (marketId: MarketId | undefined) => BigNum | undefined;
	topContent?: {
		label: string;
		description: string;
	};
}) => {
	return (
		<div
			className={cj(
				'thin-scroll overflow-hidden overflow-x-auto rounded-sm',
				'col-span-12 grid',
				'grid-cols-[minmax(244.8px,24fr)_minmax(244.8px,24fr)_minmax(137.6px,13fr)_minmax(137.6px,13fr)_minmax(137.6px,13fr)_minmax(137.6px,13fr)]'
			)}
		>
			{topContent && (
				<div className="col-span-12 flex flex-col gap-y-xs bg-button-disabled px-md py-lg">
					<div className="typo-t4 text-text-default">{topContent.label}</div>
					<div className="typo-t3 text-text-secondary">
						{topContent.description}
					</div>
				</div>
			)}
			{sortedVaultsList.length > 0 && (
				<div
					className={cj(
						'hidden',
						'px-md py-sm',
						'col-span-12',
						'typo-b4 text-text-secondary',
						'sm:grid sm:grid-cols-subgrid',
						'border-b border-stroke-divider',
						'bg-container-bg'
					)}
				>
					{getHeaders(targetApyPeriod).map((header) => {
						const isSortHeader = header.sortKey !== undefined;
						const isApy = header.sortKey === 'apy';

						const headComponent = () => {
							if (!isApy) {
								return header.label;
							}

							return (
								<Tooltip
									content={
										isNewVaults
											? `Estimated target APY, declared by the vault manager. Not inclusive of fees.`
											: `Annualised yield based on the past ${targetApyPeriod.toUpperCase()}, after deducting vault fees.`
									}
								>
									{header.label}
								</Tooltip>
							);
						};

						return (
							<div
								key={`header_${String(header.sortKey ?? header.label)}`.replace(
									/ /g,
									''
								)}
								className={cj(
									header.label === 'Action' && 'text-right',
									isSortHeader && 'flex cursor-pointer items-center gap-1'
								)}
								onClick={
									isSortHeader
										? () => {
												if (header.sortKey === sortType) {
													setSortDirection((prev) => {
														switch (prev) {
															case 'neutral':
																return 'descending';
															case 'descending':
																return 'ascending';
															case 'ascending':
																return 'neutral';
															default:
																return 'neutral';
														}
													});
												} else {
													setSortType(header.sortKey);
													setSortDirection('descending');
												}
											}
										: undefined
								}
							>
								{headComponent()}
								{isSortHeader && (
									<SortCaret
										state={
											header.sortKey === sortType ? sortDirection : 'neutral'
										}
									/>
								)}
							</div>
						);
					})}
				</div>
			)}

			{!areInitialVaultsStatsLoaded ? (
				<div className="col-span-12 flex h-50 w-full items-center justify-center sm:h-100 sm:rounded-b-sm sm:bg-container-bg">
					<InlineLoadingBar />
				</div>
			) : sortedVaultsList.length > 0 ? (
				sortedVaultsList.map((vaultStat) => (
					<VaultRow
						key={vaultStat.uiVaultConfig.vaultPubkeyString}
						vaultStat={vaultStat}
						apyPeriod={targetApyPeriod as keyof PeriodApys}
						vaultDepositorAccounts={vaultDepositorAccounts}
						getOraclePrice={getOraclePrice}
					/>
				))
			) : (
				<div className="col-span-12 flex h-[400px] w-full items-center justify-center bg-container-bg typo-b3 text-text-disabled">
					No vaults found
				</div>
			)}
		</div>
	);
};

const VaultsTableContent = ({
	sortedVaultsList,
	sortedNewVaultsList,
	areInitialVaultsStatsLoaded,
	sortType,
	setSortType,
	sortDirection,
	setSortDirection,
	targetApyPeriod,
	vaultDepositorAccounts,
	getOraclePrice,
}: {
	sortedVaultsList: VaultStatWithConfig[];
	sortedNewVaultsList: VaultStatWithConfig[];
	areInitialVaultsStatsLoaded: boolean;
	sortType: SortableHeaderType;
	setSortType: (type: SortableHeaderType) => void;
	sortDirection: SortDirection;
	setSortDirection: (direction: SetStateAction<SortDirection>) => void;
	targetApyPeriod: string;
	vaultDepositorAccounts: VaultDepositor[];
	getOraclePrice: (marketId: MarketId | undefined) => BigNum | undefined;
}) => {
	return (
		<div className="flex flex-col gap-y-lg">
			<VaultsTableBase
				sortedVaultsList={sortedVaultsList}
				areInitialVaultsStatsLoaded={areInitialVaultsStatsLoaded}
				sortType={sortType}
				setSortType={setSortType}
				sortDirection={sortDirection}
				setSortDirection={setSortDirection}
				targetApyPeriod={targetApyPeriod}
				vaultDepositorAccounts={vaultDepositorAccounts}
				getOraclePrice={getOraclePrice}
			/>

			{sortedNewVaultsList.length > 0 && (
				<>
					<Divider />
					<VaultsTableBase
						topContent={{
							label: NEW_VAULTS_TITLE,
							description: NEW_VAULTS_DESCRIPTION,
						}}
						sortedVaultsList={sortedNewVaultsList}
						areInitialVaultsStatsLoaded={areInitialVaultsStatsLoaded}
						sortType={sortType}
						setSortType={setSortType}
						sortDirection={sortDirection}
						setSortDirection={setSortDirection}
						targetApyPeriod={targetApyPeriod}
						vaultDepositorAccounts={vaultDepositorAccounts}
						isNewVaults={true}
						getOraclePrice={getOraclePrice}
					/>
				</>
			)}
		</div>
	);
};

export const VaultsTable = (props: {
	hidden: boolean;
	type: 'verified' | 'community' | 'my_positions';
	vaultDepositorAccounts: VaultDepositor[];
	targetApyPeriod?: string;
	targetVaultManager?: string;
	targetDepositAsset?: string;
}) => {
	const getOraclePrice = useGetOraclePriceForMarket();

	const { data, isLoading } = useAllVaultStats();
	const vaultsStatsList = data.list;
	const areInitialVaultsStatsLoaded = !isLoading && vaultsStatsList.length > 0;
	const getVaultDisplaySettings = useVaultsStore(
		(s) => s.getVaultDisplaySettings
	);
	const vaultDisplaySettings = getVaultDisplaySettings();

	const isMobile = useIsMobileScreenSize();
	const [balanceDisplayType, setBalanceDisplayType] =
		useState<BalanceDisplayType>('base');
	const [sortType, setSortType] = useState<SortableHeaderType>(
		vaultDisplaySettings.sortedBy
	);

	const [sortDirection, setSortDirection] =
		useState<SortDirection>('descending');
	const { targetVaultManager, targetDepositAsset, targetApyPeriod } = props;

	const toggleBalanceDisplayType = useCallback(() => {
		setBalanceDisplayType((prev) => (prev === 'base' ? 'notional' : 'base'));
	}, []);

	useEffect(() => {
		setSortType(vaultDisplaySettings.sortedBy);
	}, [vaultDisplaySettings.sortedBy]);

	const filteredVaultsStatsList = useMemo(() => {
		return vaultsStatsList
			.filter((vaultStats) => {
				return !vaultStats.uiVaultConfig.isNew(
					vaultStats.numOfVaultSnapshots ?? 0
				);
			})
			.filter((vaultStats) => {
				return props.type === 'verified'
					? vaultStats.uiVaultConfig.isVerified
					: !vaultStats.uiVaultConfig.isVerified;
			})
			.filter((vaultStats) => {
				if (targetVaultManager === 'All') {
					return true;
				}

				return (
					vaultStats.uiVaultConfig.vaultManager.name === targetVaultManager
				);
			})
			.filter((vaultStats) => {
				if (targetDepositAsset === 'All') {
					return true;
				}

				return (
					vaultStats.uiVaultConfig.depositAsset === +(targetDepositAsset ?? '0')
				);
			})
			.filter((vaultStats) => {
				const hasDeposits = props.vaultDepositorAccounts.some(
					(vaultDepositor) =>
						!!vaultDepositor &&
						vaultDepositor.vault.toString() ===
							vaultStats.uiVaultConfig.vaultPubkeyString &&
						vaultDepositor.vaultShares.gtn(0)
				);

				if (hasDeposits) return true;

				const isDeprecating = vaultStats.uiVaultConfig.isDeprecating;

				if (isDeprecating && !hasDeposits) return false;

				return true;
			});
	}, [vaultsStatsList, props.type, targetVaultManager, targetDepositAsset]);

	const sortedVaultsStatsList = filteredVaultsStatsList.sort((a, b) =>
		sortVaults(
			a,
			b,
			sortDirection,
			sortType,
			targetApyPeriod ?? '',
			props.vaultDepositorAccounts,
			getOraclePrice
		)
	);

	const filteredNewVaultsStatsList = useMemo(() => {
		return vaultsStatsList
			.filter((vaultStats) => {
				return vaultStats.uiVaultConfig.isNew(
					vaultStats.numOfVaultSnapshots ?? 0
				);
			})
			.filter((vaultStats) => {
				return props.type === 'verified'
					? vaultStats.uiVaultConfig.isVerified
					: !vaultStats.uiVaultConfig.isVerified;
			})
			.filter((vaultStats) => {
				if (targetVaultManager === 'All') {
					return true;
				}

				return (
					vaultStats.uiVaultConfig.vaultManager.name === targetVaultManager
				);
			})
			.filter((vaultStats) => {
				if (targetDepositAsset === 'All') {
					return true;
				}

				return (
					vaultStats.uiVaultConfig.depositAsset === +(targetDepositAsset ?? '0')
				);
			});
	}, [vaultsStatsList, props.type, targetVaultManager, targetDepositAsset]);

	const sortedNewVaultsStatsList = filteredNewVaultsStatsList.sort((a, b) =>
		sortVaults(
			a,
			b,
			sortDirection,
			sortType,
			targetApyPeriod ?? '',
			props.vaultDepositorAccounts,
			getOraclePrice
		)
	);

	return (
		<div
			className={cm('flex w-full flex-col gap-4', props.hidden && 'hidden')}
			data-testid={`vaults-table-${props.type}`}
		>
			{isMobile ? (
				!areInitialVaultsStatsLoaded ? (
					<div className="flex h-[400px] w-full items-center justify-center">
						<InlineLoadingBar />
					</div>
				) : (
					<div className="flex flex-col gap-4">
						{sortedVaultsStatsList.map((vaultStat) => (
							<VaultTableVaultComparisonCard
								key={vaultStat.uiVaultConfig.vaultPubkeyString}
								uiVaultConfig={vaultStat.uiVaultConfig}
								vaultStat={vaultStat}
								vaultDepositorAccounts={props.vaultDepositorAccounts}
								apyPeriod={targetApyPeriod ?? ''}
								balanceDisplayType={balanceDisplayType}
								onToggleBalanceDisplayType={toggleBalanceDisplayType}
							/>
						))}

						<div className="flex flex-col gap-1 rounded-sm bg-button-disabled p-md">
							<span className="typo-t3 text-text-default">
								{NEW_VAULTS_TITLE}
							</span>
							<span className="typo-t4 text-text-secondary">
								{NEW_VAULTS_DESCRIPTION}
							</span>
						</div>

						{sortedNewVaultsStatsList.map((vaultStat) => (
							<VaultTableVaultComparisonCard
								key={vaultStat.uiVaultConfig.vaultPubkeyString}
								uiVaultConfig={vaultStat.uiVaultConfig}
								vaultStat={vaultStat}
								vaultDepositorAccounts={props.vaultDepositorAccounts}
								apyPeriod={targetApyPeriod ?? ''}
								balanceDisplayType={balanceDisplayType}
								onToggleBalanceDisplayType={toggleBalanceDisplayType}
							/>
						))}
					</div>
				)
			) : (
				<VaultsTableContent
					sortedVaultsList={sortedVaultsStatsList}
					sortedNewVaultsList={sortedNewVaultsStatsList}
					areInitialVaultsStatsLoaded={areInitialVaultsStatsLoaded}
					sortType={sortType}
					setSortType={setSortType}
					sortDirection={sortDirection}
					setSortDirection={setSortDirection}
					targetApyPeriod={targetApyPeriod ?? ''}
					vaultDepositorAccounts={props.vaultDepositorAccounts}
					getOraclePrice={getOraclePrice}
				/>
			)}
		</div>
	);
};
